/**
 * CLOUDS — puffy inked cartoon clouds after Ghost Circuit's puff and cel recipes: an ink
 * underlay of every lobe grown by the ink width (so the outline is one clean union, no inner
 * seams), then per lobe three nested discs, like cel-art's `cel()`: the lobe in its light
 * tone, a shade disc pushed toward the lower right (leaving a lit rim on the upper left), and
 * the body disc pulled back up-left inside it (leaving a shade crescent on the lower right).
 * Each pass covers every lobe before the next, so rims survive only on the outside of the
 * cloud and small shade creases stay where lobes meet.
 *
 * A cloud is a flat-ish row of four or five bottom lobes with two or three big lobes on top. The lobes
 * breathe on the pause-aware wobble clock (radii and offsets), so clouds slowly morph as they
 * drift; the bottom row stays sitting on one line, so a cloud never loses its base.
 *
 * `layoutSkyClouds` is the one placement both renderers use (Canvas 2D paints the lobes
 * below; the Pixi sky layer places one tinted disc sprite per lobe and pass).
 */

import {
  clamp01,
  rgb,
  nightAmount,
  timeSec,
  mockupUiScaleFromOpts,
} from "./sky-utils.js";
import { combineSkyDecorSeed, seededHash01 } from "../../core/day-night.js";
import { CLOUD_TONES, mixRgb } from "../toon/sky-art.js";

export var CLOUD_COUNT = 7;
/** Most lobes a cloud can have (the GPU layer pools sprites to this). */
export var CLOUD_MAX_LOBES = 7;

/**
 * Nested lobe discs (offset as a share of the lobe radius, radius scale). The shade disc
 * touches the lobe on the lower right; the body disc touches the shade disc on the upper left.
 * Light comes from above and a little to the left.
 */
export var CLOUD_SHADE_SHIFT = [0.033, 0.05];
export var CLOUD_SHADE_SCALE = 0.94;
export var CLOUD_BODY_SHIFT = [-0.044, -0.067];
export var CLOUD_BODY_SCALE = 0.8;
/** Ink underlay offset (share of the ink width): a heavier line along the lower right. */
export var CLOUD_INK_SHIFT = [0.2, 0.45];

// The original day.html cloud was a 130×34 pill; the cartoon cloud keeps that footprint.
var BASE_BODY_W = 130;
var BASE_BODY_H = 34;

/** Ink width (px) for a cloud drawn at `unit` px per mockup px. */
export function cloudInkWidth(unit) {
  return Math.max(1.2, 2.3 * Math.max(0.05, Number(unit) || 1));
}

/**
 * Deterministic lobes for one cloud (mockup px, body top-left at 0,0, base at y = bodyH).
 * @returns {{ bodyW: number, bodyH: number, lobes: { x: number, y: number, r: number, top: boolean }[], minTop: number }}
 */
export function makeCloudShape(seed) {
  var lengthF = 0.85 + seededHash01(seed, 2.3) * 0.35;
  var bumpF = 0.92 + seededHash01(seed, 3.7) * 0.16;
  var bodyW = BASE_BODY_W * lengthF;
  var bodyH = BASE_BODY_H;
  var lobes = [];
  // Bottom row: neighbours overlap by a good quarter of a radius, so the base is one puffy
  // line however long the cloud is.
  var nB = bodyW > 138 ? 5 : 4;
  var rB = bodyW / (2 + 1.25 * (nB - 1));
  for (var i = 0; i < nB; i++) {
    var r = rB * (0.9 + seededHash01(seed, 19.3 + i * 2.7) * 0.16);
    var x = rB + (bodyW - 2 * rB) * (i / (nB - 1)) + (seededHash01(seed, 23.9 + i * 1.9) - 0.5) * rB * 0.2;
    lobes.push({ x: x, y: bodyH - r, r: r, top: false });
  }
  // Top: one big crown lobe, a second one beside it, and a small shoulder on shorter clouds.
  var big = rB * (1.28 + seededHash01(seed, 29.3) * 0.17) * bumpF;
  lobes.push({
    x: bodyW * (0.34 + seededHash01(seed, 31.1) * 0.12),
    y: bodyH - rB * 0.35 - big,
    r: big,
    top: true,
  });
  var second = rB * (1 + seededHash01(seed, 37.9) * 0.15) * bumpF;
  lobes.push({
    x: bodyW * (0.62 + seededHash01(seed, 41.3) * 0.1),
    y: bodyH - rB * 0.45 - second,
    r: second,
    top: true,
  });
  if (nB === 4) {
    var third = rB * (0.8 + seededHash01(seed, 61.7) * 0.1);
    lobes.push({
      x: bodyW * (0.14 + seededHash01(seed, 53.3) * 0.08),
      y: bodyH - rB * 0.5 - third,
      r: third,
      top: true,
    });
  }
  var minTop = 0;
  for (var k = 0; k < lobes.length; k++) {
    minTop = Math.min(minTop, lobes[k].y - lobes[k].r);
  }
  return { bodyW: bodyW, bodyH: bodyH, lobes: lobes, minTop: minTop };
}

/**
 * The lobes of `shape` at a moment of the wobble clock: each lobe breathes (radius ±10%) and
 * sways a little on its own slow beat; bottom lobes keep sitting on the base line.
 * @returns {{ x: number, y: number, r: number, top: boolean }[]}
 */
export function morphCloudLobes(shape, seed, wobbleSec) {
  var t = Number(wobbleSec) || 0;
  var out = [];
  for (var i = 0; i < shape.lobes.length; i++) {
    var l = shape.lobes[i];
    var ph = seededHash01(seed, 71.3 + i * 3.1) * Math.PI * 2;
    var w = 0.5 + seededHash01(seed, 79.7 + i * 2.3) * 0.55;
    var rr = l.r * (1 + 0.1 * Math.sin(t * w + ph));
    var x = l.x + l.r * 0.1 * Math.sin(t * w * 0.71 + ph * 1.3);
    var y = l.top
      ? l.y + l.r * 0.07 * Math.sin(t * w * 0.9 + ph * 0.7)
      : shape.bodyH - rr;
    out.push({ x: x, y: y, r: rr, top: l.top });
  }
  return out;
}

/**
 * Pixel layout for one seeded cloud at a UI scale (padded for the ink and the morph, so a
 * transparent PNG export can crop tightly without clipping).
 */
export function measureCloudLayout(cloudSeed, scaleUi) {
  var s = Math.max(0.01, Number(scaleUi) || 1);
  var h5 = seededHash01(cloudSeed, 7.19);
  var cloudScale = 0.7 + h5 * 0.5;
  var unit = s * cloudScale;
  var shape = makeCloudShape(cloudSeed);
  var bodyW = shape.bodyW * unit;
  var bodyH = shape.bodyH * unit;
  var maxR = 0;
  for (var i = 0; i < shape.lobes.length; i++) maxR = Math.max(maxR, shape.lobes[i].r);
  var grow = maxR * 0.2 * unit;
  var pad = Math.ceil(cloudInkWidth(unit) * 1.5 + grow + 2);
  var topExtent = -shape.minTop * unit + grow;
  var bodyOffsetX = pad;
  var bodyOffsetY = pad + Math.ceil(topExtent);
  return {
    unit: unit,
    shape: shape,
    cloudScale: cloudScale,
    bodyW: bodyW,
    bodyH: bodyH,
    width: Math.ceil(bodyW + pad * 2),
    height: Math.ceil(bodyOffsetY + bodyH + pad),
    bodyOffsetX: bodyOffsetX,
    bodyOffsetY: bodyOffsetY,
  };
}

/**
 * Lowest top edge a cloud may take (CSS px): ~10% into the play band, so clouds sit anywhere
 * from the top of the sky to just inside the play box. In the vertical layout tall phones open
 * empty sky between the header and the gameplay; there the band reaches down to the top of the
 * gameplay, where the Merge HP pill sits.
 * @param {{ playArea?: object, coreRect?: object, playContentArea?: object, layoutMode?: string }|null} layout
 * @param {{ x: number, y: number, width: number, height: number }} bounds
 */
export function cloudBandBottomY(layout, bounds) {
  layout = layout || {};
  var play = layout.playArea || layout.coreRect || bounds;
  var bottom = play.y + play.height * 0.10;
  var content = layout.layoutMode === "vertical" ? layout.playContentArea : null;
  if (content && content.y > bottom) bottom = content.y;
  return bottom;
}

/**
 * Deterministic cloud placement for one frame (2D draw and the GPU sky layer): drift on the
 * hour clock (`currentTime`, ms), lobe morph on the wobble clock (`opts.wobbleSec`).
 * @returns {{ seed: number, ox: number, oy: number, unit: number, bodyW: number, bodyH: number,
 *   ink: number, lobes: { x: number, y: number, r: number, top: boolean }[] }[]}
 */
export function layoutSkyClouds(bounds, currentTime, decorSeed, opts) {
  opts = opts || {};
  if (!bounds) return [];
  var x = bounds.x;
  var y = bounds.y;
  var w = Math.max(0, bounds.width);
  var h = Math.max(0, bounds.height);
  if (w <= 0 || h <= 0) return [];

  var scaleUi = mockupUiScaleFromOpts(opts, bounds);
  var t = timeSec(currentTime);
  var wobbleSec = Number(opts.wobbleSec) || 0;
  var bandTop = y + h * 0.04;
  var bandBottom = opts.cloudBandBottomY != null
    ? opts.cloudBandBottomY
    : bandTop + h * 0.14;
  var bandH = Math.max(0, bandBottom - bandTop);
  var out = [];

  for (var i = 0; i < CLOUD_COUNT; i++) {
    var cloudSeed = decorSeed != null
      ? combineSkyDecorSeed(decorSeed, i + 1)
      : i + 1;
    var h1 = seededHash01(cloudSeed, 5.11);
    var h2 = seededHash01(cloudSeed, 9.43);
    var h4 = seededHash01(cloudSeed, 4.91);
    var h5 = seededHash01(cloudSeed, 7.19);
    var shape = makeCloudShape(cloudSeed);
    var cloudScale = 0.7 + h5 * 0.5;
    var unit = scaleUi * cloudScale;
    var cloudW = shape.bodyW * unit;
    var spanW = w + cloudW * 2 + 160 * scaleUi;
    var drift = (t * (6 + h1 * 8) + h2 * spanW) % spanW;
    var ox = x - cloudW + drift;
    // One row of the band per cloud, jittered inside it: the clouds spread over the whole band
    // instead of clumping (tall vertical layouts open a deep band).
    var oy = bandTop + bandH * (i + h4) / CLOUD_COUNT;
    out.push({
      seed: cloudSeed,
      ox: ox,
      oy: oy,
      unit: unit,
      bodyW: cloudW,
      bodyH: shape.bodyH * unit,
      ink: cloudInkWidth(unit),
      lobes: placeLobes(morphCloudLobes(shape, cloudSeed, wobbleSec), ox, oy, unit),
    });
  }
  return out;
}

function placeLobes(lobes, ox, oy, unit) {
  var out = [];
  for (var i = 0; i < lobes.length; i++) {
    var l = lobes[i];
    out.push({ x: ox + l.x * unit, y: oy + l.y * unit, r: l.r * unit, top: l.top });
  }
  return out;
}

/** Centre of a placed cloud (for the celestial light falloff). */
export function cloudCenter(cloud) {
  return { x: cloud.ox + cloud.bodyW * 0.5, y: cloud.oy + cloud.bodyH * 0.2 };
}

/**
 * Flat tones of one cloud for the time of day: day white ↔ night slate by the day/night sweep,
 * a golden-hour wash, and a warm (sun) or silver (moon) lift when the body is near.
 * @param {{ cloudDayBoundary?: number, timeOfDay?: number, cloudWarmth?: number,
 *   cloudWarmColors?: object, cloudLight?: object|null }} look
 * @param {{ x: number, y: number }} [center] cloud centre for the light falloff
 * @returns {{ ink: number[], shade: number[], base: number[], light: number[] }}
 */
export function cloudTones(look, center) {
  var o = look || {};
  var m = o.cloudDayBoundary != null
    ? clamp01(o.cloudDayBoundary)
    : nightAmount(o.timeOfDay != null ? o.timeOfDay : 0.25);
  m = m * m * (3 - 2 * m);
  var D = CLOUD_TONES.day;
  var N = CLOUD_TONES.night;
  var tones = {
    ink: mixRgb(D.ink, N.ink, m),
    shade: mixRgb(D.shade, N.shade, m),
    base: mixRgb(D.base, N.base, m),
    light: mixRgb(D.light, N.light, m),
  };
  var warmth = clamp01(o.cloudWarmth || 0);
  var W = o.cloudWarmColors && o.cloudWarmColors.base ? o.cloudWarmColors : CLOUD_TONES.sunset;
  if (warmth > 0.002) {
    // Warm light paints over the day/night mix: gold or rose while the sky is light, lilac
    // once it darkens, so golden hour never passes through grey.
    var duskK = clamp01((m - 0.45) / 0.55);
    var D2 = CLOUD_TONES.dusk;
    var k = warmth * 0.9;
    tones.ink = mixRgb(tones.ink, mixRgb(W.ink, D2.ink, duskK), k);
    tones.shade = mixRgb(tones.shade, mixRgb(W.shade, D2.shade, duskK), k);
    tones.base = mixRgb(tones.base, mixRgb(W.base, D2.base, duskK), k);
    tones.light = mixRgb(tones.light, mixRgb(W.light, D2.light, duskK), k);
  }
  var L = o.cloudLight;
  if (L && center && L.glowRadius > 0 && L.strength > 0) {
    var dx = center.x - L.x;
    var dy = center.y - L.y;
    var fall = 1 - clamp01(Math.sqrt(dx * dx + dy * dy) / L.glowRadius);
    var amt = clamp01(L.strength * fall);
    if (amt > 0.002) {
      tones.base = mixRgb(tones.base, L.color, amt * 0.1);
      tones.light = mixRgb(tones.light, L.color, amt * 0.55);
    }
  }
  return tones;
}

function circles(ctx, lobes, grow, sx, sy, scale) {
  ctx.beginPath();
  for (var i = 0; i < lobes.length; i++) {
    var l = lobes[i];
    var r = Math.max(0, l.r * scale + grow);
    var cx = l.x + sx * l.r;
    var cy = l.y + sy * l.r;
    ctx.moveTo(cx + r, cy);
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
  }
}

/**
 * Paint placed lobes: ink underlay, then light, shade and body passes over every lobe.
 * The Pixi sky layer does the same passes with tinted disc sprites.
 */
export function paintCloudLobes(ctx, lobes, tones, ink) {
  if (!ctx || !lobes || !lobes.length) return;
  var iw = Math.max(0, Number(ink) || 0);
  // Ink: every lobe grown by the ink width, nudged down-right, as one union fill.
  ctx.beginPath();
  for (var i = 0; i < lobes.length; i++) {
    var l = lobes[i];
    var r = l.r + iw;
    var cx = l.x + iw * CLOUD_INK_SHIFT[0];
    var cy = l.y + iw * CLOUD_INK_SHIFT[1];
    ctx.moveTo(cx + r, cy);
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
  }
  ctx.fillStyle = rgb(tones.ink);
  ctx.fill();
  circles(ctx, lobes, 0, 0, 0, 1);
  ctx.fillStyle = rgb(tones.light);
  ctx.fill();
  circles(ctx, lobes, 0, CLOUD_SHADE_SHIFT[0], CLOUD_SHADE_SHIFT[1], CLOUD_SHADE_SCALE);
  ctx.fillStyle = rgb(tones.shade);
  ctx.fill();
  circles(ctx, lobes, 0, CLOUD_BODY_SHIFT[0], CLOUD_BODY_SHIFT[1], CLOUD_BODY_SCALE);
  ctx.fillStyle = rgb(tones.base);
  ctx.fill();
}

/**
 * Draw one cloud with body top-left at `(ox, oy)`. Shape/size come from `cloudSeed`.
 * Time-of-day look uses the same opts as `drawClouds` (`timeOfDay`, `cloudDayBoundary`,
 * `cloudWarmth`, `cloudWarmColors`, `cloudLight`); `wobbleSec` morphs the lobes.
 * Pass `scaleUi` (or `layout` + `bounds`) to size the cloud.
 *
 * @param {CanvasRenderingContext2D} ctx
 * @param {number} ox body left edge
 * @param {number} oy body top edge
 * @param {number} cloudSeed
 * @param {object} [opts]
 */
export function drawSingleCloud(ctx, ox, oy, cloudSeed, opts) {
  if (!ctx) return;
  opts = opts || {};
  var scaleUi = opts.scaleUi;
  if (!(scaleUi > 0)) {
    scaleUi = mockupUiScaleFromOpts(opts, opts.bounds || { width: 720, height: 400 });
  }
  var h5 = seededHash01(cloudSeed, 7.19);
  var unit = scaleUi * (0.7 + h5 * 0.5);
  var shape = makeCloudShape(cloudSeed);
  var lobes = placeLobes(morphCloudLobes(shape, cloudSeed, opts.wobbleSec), ox, oy, unit);
  var tones = cloudTones(opts, { x: ox + shape.bodyW * unit * 0.5, y: oy + shape.bodyH * unit * 0.2 });
  paintCloudLobes(ctx, lobes, tones, cloudInkWidth(unit));
}

/**
 * @param {CanvasRenderingContext2D} ctx
 * @param {{ x: number, y: number, width: number, height: number }} bounds
 * @param {number} currentTime hour-clock ms (drift)
 * @param {number} decorSeed
 * @param {object} [opts] layout, cloudBandBottomY, wobbleSec + the look opts of drawSingleCloud
 */
export function drawClouds(ctx, bounds, currentTime, decorSeed, opts) {
  if (!ctx || !bounds) return;
  opts = opts || {};
  var placed = layoutSkyClouds(bounds, currentTime, decorSeed, opts);
  for (var i = 0; i < placed.length; i++) {
    var c = placed[i];
    paintCloudLobes(ctx, c.lobes, cloudTones(opts, cloudCenter(c)), c.ink);
  }
}
